/**
 * Effective-DEA-schedule resolution.
 *
 * An item's effective schedule for a given shipping address is determined by
 * its `item_dea_schedule` rows:
 *   1. The state-level row matching the address state wins (licenseLevel='State').
 *   2. Otherwise the federal row applies (licenseLevel='Federal').
 *   3. Otherwise the item is unscheduled (returns null).
 *
 * Schedule strings can arrive mixed-case from legacy imports (e.g. 'Rx',
 * 'c5'); resolution is case-insensitive on `licenseLevel` but the returned
 * `schedule` is whatever was stored — callers normalise when comparing.
 */

export interface DeaScheduleRow {
	licenseLevel: string | null;
	state: string | null;
	schedule: string | null;
}

export function resolveEffectiveSchedule(
	rows: ReadonlyArray<DeaScheduleRow> | null | undefined,
	addressState: string | null | undefined,
): DeaScheduleRow | null {
	if (rows == null || rows.length === 0) {
		return null;
	}

	if (addressState != null && addressState !== '') {
		const normalizedState = addressState.toUpperCase();
		const stateMatch = rows.find(
			(r) =>
				r.licenseLevel != null &&
				r.licenseLevel.toUpperCase() === 'STATE' &&
				r.state != null &&
				r.state.toUpperCase() === normalizedState,
		);
		if (stateMatch != null) {
			return stateMatch;
		}
	}

	const federal = rows.find(
		(r) => r.licenseLevel != null && r.licenseLevel.toUpperCase() === 'FEDERAL',
	);
	return federal ?? null;
}

const CONTROLLED_PREFIXES: ReadonlyArray<string> = [
	'C2',
	'C2N',
	'C3',
	'C3N',
	'C4',
	'C5',
];

export function isControlledSchedule(
	schedule: string | null | undefined,
): boolean {
	if (schedule == null) {
		return false;
	}
	const normalized = schedule.toUpperCase();
	return CONTROLLED_PREFIXES.includes(normalized);
}

export function isRxSchedule(schedule: string | null | undefined): boolean {
	if (schedule == null) {
		return false;
	}
	return schedule.toUpperCase() === 'RX';
}
